import { useState } from 'react';
import { isAxiosError } from 'axios';
import type {
  ScoCardEodCommandView,
  ScoCardEodDecision,
  ScoCardEodView,
} from '@reform-lite/contracts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useHasPermission } from '@/auth/auth-store';
import { useIssueScoCardEod, useScoCardEod } from '@/hooks/use-sco-card-eod';
import { useT } from '@/i18n/use-t';

type Duty = ScoCardEodView['duties'][number];
type Translate = ReturnType<typeof useT>;

/** The lane's reason codes an operator gets words for; others show as the code. */
function reasonText(t: Translate, reason: string | null): string {
  switch (reason) {
    case null:
      return '—';
    case 'dispatch_unknown':
      return t(
        'terminals.cardEod.reason.dispatchUnknown',
        'sent, no answer from the card terminal',
      );
    case 'interrupted':
      return t('terminals.cardEod.reason.interrupted', 'interrupted by a restart');
    case 'fiscal_close_unknown':
      return t(
        'terminals.cardEod.reason.fiscalCloseUnknown',
        'the fiscal day close is not confirmed',
      );
    case 'terminal_changed':
      return t('terminals.cardEod.reason.terminalChanged', 'the card terminal changed');
    case 'journal_unreadable':
      return t(
        'terminals.cardEod.reason.journalUnreadable',
        'the end-of-day journal could not be read',
      );
    case 'staff_not_settled':
      return t('terminals.cardEod.reason.staffNotSettled', 'marked not settled; owed again');
    default:
      return reason;
  }
}

function decisionText(t: Translate, decision: ScoCardEodDecision): string {
  switch (decision) {
    case 'settled':
      return t('terminals.cardEod.decision.settled', 'settled');
    case 'not_settled':
      return t('terminals.cardEod.decision.notSettled', 'send again');
    case 'simulator_discarded':
      return t('terminals.cardEod.decision.discarded', 'simulator discarded');
  }
}

function when(iso: string | null): string {
  if (iso === null) return '—';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'short', timeStyle: 'short' }).format(
    new Date(iso),
  );
}

function errorText(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const body = error.response?.data as { message?: unknown } | undefined;
    if (typeof body?.message === 'string') return body.message;
  }
  return fallback;
}

/**
 * MICRO-46 — a micromarket lane's card end-of-day duties, as it last reported
 * them, and the operator's decision on an uncertain one (owner, 2026-10-23):
 * "settled" only after checking the bank / EPS batch report, whose date and
 * number are typed and stored; "send again" only for the card terminal the
 * lane uses now; "discard" only for a simulator. The lane collects the
 * decision on its next report and checks it again against its own journal.
 *
 * Renders nothing for a lane that never reported, so every other lane's page
 * is unchanged.
 */
export function CardEodPanel({ terminalId }: { terminalId: string }): JSX.Element | null {
  const t = useT('admin_ui');
  const query = useScoCardEod(terminalId);
  const canDecide = useHasPermission('sco-card-eod.decide');
  const view = query.data;
  if (view === undefined || view.reportedAt === null) return null;

  return (
    <section className="rounded-md border bg-card p-5" data-testid="card-eod-panel">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-medium">{t('terminals.cardEod.title', 'Card end of day')}</h2>
        <span className="text-xs text-muted-foreground">
          {t('terminals.cardEod.reportedAt', 'Reported')} {when(view.reportedAt)}
        </span>
      </div>
      {!view.fresh && (
        <p role="status" className="mb-2 text-xs text-destructive">
          {t(
            'terminals.cardEod.stale',
            'The lane has not reported recently; decisions wait until it reports again.',
          )}
        </p>
      )}
      {view.duties.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          {t('terminals.cardEod.empty', 'No card end of day is waiting.')}
        </p>
      ) : (
        <ul className="space-y-3">
          {view.duties.map((duty) => (
            <DutyRow
              key={duty.dutyId}
              terminalId={terminalId}
              duty={duty}
              decidable={canDecide && view.fresh}
              currentCardTerminal={view.currentCardTerminal}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function DutyRow({
  terminalId,
  duty,
  decidable,
  currentCardTerminal,
}: {
  terminalId: string;
  duty: Duty;
  decidable: boolean;
  currentCardTerminal: string | null;
}): JSX.Element {
  const t = useT('admin_ui');
  const [choice, setChoice] = useState<ScoCardEodDecision | null>(null);
  const waiting = duty.commands.find((command) => command.completedAt === null);
  const last = duty.commands.find((command) => command.completedAt !== null);
  const canChoose = decidable && duty.state === 'uncertain' && waiting === undefined;

  return (
    <li className="rounded-md border p-3 text-xs" data-testid={`card-eod-duty-${duty.dutyId}`}>
      <div className="font-medium">
        {t('terminals.cardEod.duty', 'Fiscal day {day} · terminal {terminal}')
          .replace('{day}', duty.fiscalDay === null ? '—' : String(duty.fiscalDay))
          .replace('{terminal}', duty.cardTerminal ?? '—')}
      </div>
      <div className="text-muted-foreground">
        {duty.state} · {t('terminals.cardEod.reason', 'Reason')}: {reasonText(t, duty.reason)} ·{' '}
        {t('terminals.cardEod.since', 'since')} {when(duty.since)} ·{' '}
        {t('terminals.cardEod.attempts', 'attempts')}: {duty.attempts}
        {duty.lastErrorKey !== null && ` · ${duty.lastErrorKey}`}
      </div>
      {waiting !== undefined && <CommandLine command={waiting} waiting />}
      {waiting === undefined && last !== undefined && (
        <CommandLine command={last} waiting={false} />
      )}
      {canChoose && choice === null && (
        <div className="mt-2 flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={() => setChoice('settled')}>
            {t('terminals.cardEod.settled', 'Settled — I checked the bank / EPS report')}
          </Button>
          {duty.cardTerminal !== null && duty.cardTerminal === currentCardTerminal && (
            <Button type="button" variant="outline" onClick={() => setChoice('not_settled')}>
              {t('terminals.cardEod.notSettled', 'Not settled — Send again')}
            </Button>
          )}
          {duty.simulator && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setChoice('simulator_discarded')}
            >
              {t('terminals.cardEod.discard', 'Discard (simulator)')}
            </Button>
          )}
        </div>
      )}
      {canChoose && choice !== null && (
        <DecisionForm
          terminalId={terminalId}
          duty={duty}
          decision={choice}
          onDone={() => setChoice(null)}
        />
      )}
    </li>
  );
}

function CommandLine({
  command,
  waiting,
}: {
  command: ScoCardEodCommandView;
  waiting: boolean;
}): JSX.Element {
  const t = useT('admin_ui');
  const report =
    command.bankReport === null ? '' : ` · ${command.bankReport.date} ${command.bankReport.number}`;
  return (
    <p className="mt-1 text-xs">
      {waiting
        ? t('terminals.cardEod.waiting', 'Waiting for the lane')
        : `${t('terminals.cardEod.outcome', 'Lane answered')}: ${command.outcome ?? '—'}`}
      {': '}
      {decisionText(t, command.decision)} · {command.actor.name} · {when(command.issuedAt)}
      {report}
    </p>
  );
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;

function DecisionForm({
  terminalId,
  duty,
  decision,
  onDone,
}: {
  terminalId: string;
  duty: Duty;
  decision: ScoCardEodDecision;
  onDone: () => void;
}): JSX.Element {
  const t = useT('admin_ui');
  const issue = useIssueScoCardEod();
  const [checked, setChecked] = useState(false);
  const [date, setDate] = useState('');
  const [number, setNumber] = useState('');
  const [error, setError] = useState<string | null>(null);
  const settled = decision === 'settled';
  const ready = !settled || (checked && DATE.test(date) && number.trim() !== '');

  async function confirm(): Promise<void> {
    setError(null);
    try {
      await issue.mutateAsync({
        terminalId,
        dutyId: duty.dutyId,
        decision,
        bankReportChecked: settled ? checked : false,
        ...(settled ? { bankReportDate: date, bankReportNumber: number.trim() } : {}),
      });
      onDone();
    } catch (cause) {
      setError(errorText(cause, t('terminals.cardEod.error', 'The decision was not saved.')));
    }
  }

  return (
    <div className="mt-2 space-y-2 rounded-md border p-2">
      <p>
        {settled
          ? t(
              'terminals.cardEod.settledConfirm',
              'Only after checking the bank or EPS batch report: the card payments of this fiscal day are in a settled batch.',
            )
          : decision === 'not_settled'
            ? t(
                'terminals.cardEod.notSettledConfirm',
                'The batch is not settled. The lane sends the end of day again when it is idle.',
              )
            : t(
                'terminals.cardEod.discardConfirm',
                'This terminal is a simulator and holds no bank batch. The end of day is dropped and nothing is sent.',
              )}
      </p>
      {settled && (
        <>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={checked}
              onChange={(event) => setChecked(event.target.checked)}
            />
            {t('terminals.cardEod.checked', 'I checked the bank / EPS batch report')}
          </label>
          <label className="flex items-center gap-2">
            <span>{t('terminals.cardEod.reportDate', 'Report date (YYYY-MM-DD)')}</span>
            <Input value={date} onChange={(event) => setDate(event.target.value)} />
          </label>
          <label className="flex items-center gap-2">
            <span>{t('terminals.cardEod.reportNumber', 'Report number')}</span>
            <Input value={number} onChange={(event) => setNumber(event.target.value)} />
          </label>
        </>
      )}
      {error !== null && (
        <p role="alert" className="text-destructive">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <Button type="button" disabled={!ready || issue.isPending} onClick={() => void confirm()}>
          {t('terminals.cardEod.confirm', 'Confirm')}
        </Button>
        <Button type="button" variant="outline" onClick={onDone}>
          {t('terminals.cardEod.cancel', 'Cancel')}
        </Button>
      </div>
    </div>
  );
}
